import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { chatApi, notesApi } from '@/lib/api';
import { useLanguage } from '@/context/LanguageContext';
import { toast } from 'sonner';
import {
  Send, Mic, MicOff, Loader2, Bot, User,
  Bookmark, RefreshCw, History, Search, Trash2, MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';

export default function ChatPage() {
  const { t } = useLanguage();
  const [messages, setMessages] = useState([
    { role: 'assistant', content: t('chat_welcome') }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const scrollRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const loadSessions = async (search = '') => {
    setSessionsLoading(true);
    try {
      const res = await chatApi.getSessions({ search, limit: 50 });
      setSessions(res.data.sessions || []);
    } catch { /* ignore */ }
    finally { setSessionsLoading(false); }
  };

  useEffect(() => {
    if (showHistory) loadSessions(searchQuery);
  }, [showHistory]);

  const handleSearchSessions = () => loadSessions(searchQuery);

  const loadSession = async (sid) => {
    try {
      const res = await chatApi.getSession(sid);
      const msgs = (res.data.messages || []).map(m => ({ role: m.role, content: m.content }));
      setMessages(msgs.length ? msgs : [{ role: 'assistant', content: t('chat_welcome') }]);
      setSessionId(sid);
      setShowHistory(false);
    } catch { toast.error('Failed to load session'); }
  };

  const deleteSession = async (sid) => {
    try {
      await chatApi.deleteSession(sid);
      setSessions(prev => prev.filter(s => s.id !== sid));
      if (sessionId === sid) { setSessionId(null); clearChat(); }
      toast.success('Session deleted');
    } catch { toast.error('Failed to delete session'); }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);
    try {
      const history = messages.map(m => ({ role: m.role, content: m.content }));
      const response = await chatApi.send(userMessage, history, sessionId);
      const data = response.data;
      setMessages(prev => [...prev, {
        role: 'assistant', content: data.response,
        sentiment: data.sentiment, shouldLog: data.should_log,
        suggestedNote: data.suggested_note
      }]);
      // If it's a new session, store the session_id from the first message context
      if (!sessionId) {
        // The backend creates a session automatically; reload sessions to get the ID
        const sessRes = await chatApi.getSessions({ limit: 1 });
        if (sessRes.data.sessions?.length) setSessionId(sessRes.data.sessions[0].id);
      }
    } catch {
      toast.error('Failed to get response');
      setMessages(prev => [...prev, { role: 'assistant', content: "I'm having trouble connecting right now. Please try again." }]);
    } finally { setLoading(false); }
  };

  const handleSaveAsNote = async (content) => {
    try {
      await notesApi.create({ content, type: 'text', mood: null });
      toast.success(t('chat_save_insight'));
    } catch { toast.error('Failed to save note'); }
  };

  const startVoiceInput = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = () => { stream.getTracks().forEach(track => track.stop()); toast.info('Voice captured.'); };
      mediaRecorder.start();
      setRecording(true);
    } catch { toast.error('Could not access microphone'); }
  };

  const stopVoiceInput = () => {
    if (mediaRecorderRef.current && recording) { mediaRecorderRef.current.stop(); setRecording(false); }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const clearChat = () => {
    setMessages([{ role: 'assistant', content: t('chat_cleared') }]);
    setSessionId(null);
  };

  return (
    <div className="h-[calc(100vh-8rem)] md:h-[calc(100vh-4rem)] flex flex-col" data-testid="chat-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-heading text-2xl text-white">{t('chat_title')}</h1>
          <p className="text-slate-400 text-sm">{t('chat_subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => setShowHistory(!showHistory)} className={`text-slate-400 hover:text-white ${showHistory ? 'bg-cyan/10 text-cyan' : ''}`} data-testid="chat-history-btn">
            <History size={20} />
          </Button>
          <Button variant="ghost" size="icon" onClick={clearChat} className="text-slate-400 hover:text-white" data-testid="clear-chat-btn">
            <RefreshCw size={20} />
          </Button>
        </div>
      </div>

      {showHistory ? (
        <div className="flex-1 flex flex-col">
          <h2 className="text-lg text-white font-medium mb-1">{t('chat_history')}</h2>
          <p className="text-slate-500 text-sm mb-4">{t('chat_history_subtitle')}</p>
          <div className="flex gap-2 mb-4">
            <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearchSessions()}
              placeholder={t('chat_search_placeholder')} className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500" data-testid="chat-search-input" />
            <Button onClick={handleSearchSessions} className="bg-cyan text-navy-950 hover:bg-cyan-300"><Search size={16} /></Button>
          </div>
          <ScrollArea className="flex-1">
            {sessionsLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="animate-spin text-cyan" size={24} /></div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <MessageSquare size={40} className="mx-auto mb-3 opacity-30" />
                <p>{t('chat_no_history')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {sessions.map((s) => (
                  <div key={s.id} className="glass rounded-xl p-4 flex items-center justify-between hover:bg-white/5 transition-colors" data-testid={`chat-session-${s.id}`}>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => loadSession(s.id)}>
                      <p className="text-white font-medium truncate">{s.title}</p>
                      <p className="text-slate-500 text-xs mt-1">{new Date(s.updated_at).toLocaleDateString()} &middot; {s.message_count || 0} messages</p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => deleteSession(s.id)} className="text-slate-500 hover:text-red-400 flex-shrink-0 ml-2">
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      ) : (
        <>
          <ScrollArea ref={scrollRef} className="flex-1 pr-4">
            <div className="space-y-4 pb-4">
              <AnimatePresence initial={false}>
                {messages.map((message, index) => (
                  <motion.div key={index} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {message.role === 'assistant' && (
                      <div className="w-10 h-10 rounded-full bg-cyan/20 flex items-center justify-center flex-shrink-0"><Bot className="text-cyan" size={20} /></div>
                    )}
                    <div className={`max-w-[80%] ${message.role === 'user' ? 'order-first' : ''}`}>
                      <div className={`rounded-2xl p-4 ${message.role === 'user' ? 'bg-cyan text-navy-950 rounded-tr-none' : 'bg-navy-900/60 text-slate-200 rounded-tl-none border border-white/5'}`}>
                        <p className="whitespace-pre-wrap">{message.content}</p>
                      </div>
                      {message.role === 'assistant' && message.shouldLog && message.suggestedNote && (
                        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mt-2">
                          <Button variant="ghost" size="sm" onClick={() => handleSaveAsNote(message.suggestedNote)} className="text-gold hover:text-gold-muted hover:bg-gold/10" data-testid="save-note-btn">
                            <Bookmark size={16} className="mr-1" />{t('chat_save_insight')}
                          </Button>
                        </motion.div>
                      )}
                    </div>
                    {message.role === 'user' && (
                      <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0"><User className="text-gold" size={20} /></div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
              {loading && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-3">
                  <div className="w-10 h-10 rounded-full bg-cyan/20 flex items-center justify-center"><Bot className="text-cyan" size={20} /></div>
                  <div className="bg-navy-900/60 rounded-2xl rounded-tl-none p-4 border border-white/5">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-cyan rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-cyan rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-cyan rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </ScrollArea>
          <div className="mt-4 flex gap-2">
            <Button variant="outline" size="icon" onClick={recording ? stopVoiceInput : startVoiceInput} className={`flex-shrink-0 ${recording ? 'bg-red-500 border-red-500 text-white' : 'border-white/10 text-slate-400'}`} data-testid="voice-input-btn">
              {recording ? <MicOff size={20} /> : <Mic size={20} />}
            </Button>
            <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyPress={handleKeyPress} placeholder={t('chat_placeholder')} disabled={loading} className="flex-1 bg-navy-900 border-white/10 text-white placeholder:text-slate-500" data-testid="chat-input" />
            <Button onClick={handleSend} disabled={loading || !input.trim()} className="bg-cyan text-navy-950 hover:bg-cyan-300" data-testid="send-btn">
              {loading ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
